import React, { useState } from "react";
import { Modal, Button, Form, Alert, Badge, Spinner } from "react-bootstrap";
import { ListingDetailEntity } from "../dto/listing/ListingDetailEntity";
import { cancelListing } from "../controller/ListingController";
import toast from "react-hot-toast";

interface Props {
    show: boolean;
    listingDetail: ListingDetailEntity;
    onClose: () => void;
    onSuccess: () => void;
}

const CONFIRM_PHRASE = "CANCEL AUCTION";

export const ListingCancelModal: React.FC<Props> = ({ show, listingDetail, onClose, onSuccess }) => {
    const [reason, setReason] = useState("");
    const [confirmText, setConfirmText] = useState("");
    const [loading, setLoading] = useState(false);

    const listing = listingDetail.listing;
    const confirmed = confirmText === CONFIRM_PHRASE;
    const hasWinningBid = !!listing?.winningBidId;

    const handleClose = () => {
        if (loading) return;
        setReason("");
        setConfirmText("");
        onClose();
    };

    const handleSubmit = async () => {
        if (!listing?.id || !confirmed || !reason.trim()) return;
        setLoading(true);
        try {
            const response = await cancelListing(listing.id, reason.trim());
            if (response.status !== 0) {
                toast.error(response.msg);
            } else {
                toast.success(`Listing #${listing.id} cancelled.`);
                handleClose();
                onSuccess();
            }
        } catch (e: any) {
            toast.error(e.message ?? "Unexpected error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop={loading ? "static" : true}>
            <Modal.Header style={{ background: "#f8d7da", borderBottom: "2px solid #dc3545" }}>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "1.3rem" }}>🚫</span>
                    <span style={{ fontWeight: 700, color: "#58151c" }}>Cancel Auction</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Listing summary */}
                <div style={{
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: 8,
                    padding: "12px 16px",
                    marginBottom: 16,
                }}>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-1">
                        <div>
                            <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                Listing #{listing?.id}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                                {listing?.productTitle ?? "—"}
                            </div>
                        </div>
                        <Badge bg="secondary">{listing?.status}</Badge>
                    </div>
                    <div className="mt-2" style={{ fontSize: "0.85rem", color: "#495057" }}>
                        Seller: <strong>{listing?.accountNickname ?? listing?.accountPhoneNo ?? "—"}</strong>
                        &nbsp;·&nbsp; Bids: <strong>{listing?.bidCount ?? 0}</strong>
                    </div>
                </div>

                {/* Winning bid refund notice */}
                {hasWinningBid ? (
                    <Alert variant="warning" className="py-2 px-3" style={{ fontSize: "0.87rem" }}>
                        The current winning bid (ID: <strong>{listing?.winningBidId}</strong>) is on hold.
                        Cancelling will <strong>release that hold</strong> back to the bidder's wallet.
                    </Alert>
                ) : (
                    <Alert variant="info" className="py-2 px-3" style={{ fontSize: "0.87rem" }}>
                        No winning bid on this listing — no wallet refund will occur.
                    </Alert>
                )}

                {/* Reason */}
                <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 600 }}>
                        Reason <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        placeholder="e.g. Inappropriate product, fraudulent item description…"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={loading}
                    />
                    <Form.Text className="text-muted">{reason.length}/500</Form.Text>
                </Form.Group>

                {/* Confirm phrase */}
                <Form.Group>
                    <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                        Type <strong>cancel auction</strong> to confirm
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder={CONFIRM_PHRASE}
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                        disabled={loading}
                        style={{
                            borderColor: confirmText.length > 0
                                ? (confirmed ? "#198754" : "#dc3545")
                                : undefined,
                        }}
                    />
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Go Back
                </Button>
                <Button
                    variant="danger"
                    onClick={handleSubmit}
                    disabled={!confirmed || !reason.trim() || loading}
                >
                    {loading
                        ? <><Spinner size="sm" animation="border" className="me-2" />Cancelling…</>
                        : "Cancel Auction"
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};